import { useEffect, useState, useRef } from 'react'
import { isEmpty, length } from 'ramda'
import { Trans, useTranslation } from 'react-i18next'
import {
  Box,
  Stack,
  Text,
  TextLinkButton,
  Divider,
  Skeleton,
  useToast
} from '@machobear/design-system'
import useDryRun from '@/hooks/use-dry-run'
import usePayments from '@/hooks/use-payments'
import { InvoiceTypeItem, DiscountType } from './types'
import { usePaymentContext } from '../payment-context'
import CouponField from '../coupon-field'

type Props = {
  state: string
  country: string
  setIsLoading: () => void
  setLoadingFinished: () => void
}

const getFormattedCountry = (country: string = '') => {
  return country?.toUpperCase()
}

const getCurrentCouponInfo = (discounts: Array<DiscountType>, code: string) => {
  const discount = discounts?.filter(d => d?.promoCode === code)[0]

  return discount
}

const SummaryItems = ({ state, country, setIsLoading, setLoadingFinished }: Props) => {
  const { t } = useTranslation()
  const { loading } = usePayments()
  const showToast = useToast()
  const { coupon, setCoupon, product, couponApplyCallback } = usePaymentContext()
  const couponRef = useRef<DiscountType | null>(null)
  const [showCouponErr, setShowCouponErr] = useState(false)
  const [enterDiscount, setEnterDiscount] = useState(!isEmpty(coupon))
  const formattedCountry = getFormattedCountry(country)

  const {
    data: productData,
    isLoading,
    isFetching
  } = useDryRun({
    items: product.items,
    metadata: product?.metadata,
    coupon,
    state,
    country: formattedCountry
  })

  const showLoading = isFetching || loading || isLoading

  useEffect(() => {
    const currentDiscountInfo = getCurrentCouponInfo(productData?.discounts, coupon)
    const isCouponApplied = length(productData?.discounts) > 0
    if (isCouponApplied) {
      couponRef.current = currentDiscountInfo
    } else {
      couponRef.current = null
    }
  }, [coupon, productData?.discounts])

  useEffect(() => {
    if (showLoading) {
      setIsLoading()
    } else {
      setLoadingFinished()
    }
  }, [showLoading, setIsLoading, setLoadingFinished])

  useEffect(() => {
    const isAppliedCouponInValid = couponRef.current && !couponRef.current?.isValid
    const showCouponError = !showLoading && isAppliedCouponInValid

    if (showCouponError) {
      setShowCouponErr(true)
      setCoupon('')
      showToast({ message: 'The code entered is invalid or expired.', tone: 'critical' })
    }
  }, [setCoupon, showLoading, showToast])

  const handleCouponClear = () => {
    setShowCouponErr(false)
  }

  // Call Coupon Apply Callback when coupon is applied and dry run is successful
  useEffect(() => {
    const isCouponValid = couponRef.current && couponRef.current?.isValid
    if (!isEmpty(coupon) && isCouponValid && !showLoading) {
      couponApplyCallback()
    }
  }, [coupon, couponApplyCallback, showLoading])

  return (
    <Stack space="small">
      {productData?.items?.map((item: InvoiceTypeItem) => {
        const quantity = item?.quantity
        return (
          <Box display="flex" justifyContent="spaceBetween" alignItems="center" key={item?.priceId}>
            {showLoading ? (
              <Skeleton.Shape height={11} width={'70%'} />
            ) : (
              <Box display="flex" alignItems="flexStart" gap="xxxsmall">
                <Text size="small" tone="secondary">
                  {quantity} x
                </Text>
                <Box display="flex" flexDirection="column" gap="xxsmall">
                  <Text size="small" tone="secondary">
                    {item?.prettyProductName}
                  </Text>
                  {item?.useCredits && (
                    <Text size="xsmall" tone="positive">
                      <Trans i18nKey="l.checkout_modal.credits_applied.text" quantity={quantity}>
                        {{ quantity }} credit(s) applied!
                      </Trans>
                    </Text>
                  )}
                </Box>
              </Box>
            )}
            {showLoading ? (
              <Skeleton.Shape height={11} width={'25%'} />
            ) : (
              <Box display="flex" alignItems="center" gap="xxxsmall">
                {item?.useCredits && (
                  <Text size="small" tone="secondary">
                    <Box component="s">{`$${item?.amount}`}</Box>
                  </Text>
                )}
                <Text size="small" tone="secondary">{`$${item?.value}`}</Text>
              </Box>
            )}
          </Box>
        )
      })}
      <Divider />
      <Box display="flex" justifyContent="spaceBetween" alignItems="center">
        <Text size="small" tone="secondary">
          {t('b.payment.view.subtotal.label', 'Subtotal')}
        </Text>

        {showLoading ? (
          <Skeleton.Shape height={11} width={'25%'} />
        ) : (
          <Text size="small" tone="secondary">{`CA$${productData?.total_excluding_discount}`}</Text>
        )}
      </Box>
      <Box display="flex" justifyContent="spaceBetween" width="full">
        {enterDiscount ? (
          <CouponField
            discount={productData?.discount}
            isFetching={isFetching}
            couponError={showCouponErr}
            onClear={handleCouponClear}
          />
        ) : (
          <TextLinkButton onClick={() => setEnterDiscount(true)}>
            {t('b.payment.view.add_coupon.button', 'Add coupon')}
          </TextLinkButton>
        )}
      </Box>
      <Box display="flex" justifyContent="spaceBetween" alignItems="center">
        <Text size="xsmall" tone="tertiary">
          GST/HST
        </Text>
        {showLoading ? (
          <Skeleton.Shape height={11} width={'25%'} />
        ) : (
          <Text size="xsmall" tone="tertiary">
            {state === '' ? `--` : `$${productData?.tax}`}
          </Text>
        )}
      </Box>
      <Divider weight="strong" />
      <Box display="flex" justifyContent="spaceBetween">
        <Text size="standard" weight="strong">
          {t('common.total', 'Total')}
        </Text>
        {showLoading ? (
          <Skeleton.Shape height={11} width={'25%'} />
        ) : (
          <Text size="standard" weight="strong">
            {state === '' ? `$${productData?.total_excluding_tax}` : `$${productData?.total}`}
          </Text>
        )}
      </Box>
    </Stack>
  )
}

export default SummaryItems
